<template>
	<app-title-bar
		:icon="['fas', 'fa-clipboard-check']"
		subtitle="Yard"
		title="Assign Stock Check"
		:badges="[contract?.contract_number, contract?.account_name]">
		<div class="flex gap-3">
			<app-button to="/yard" icon="fa-circle-left"> Back </app-button>

			<resource-details :loading="loading || !contract" :resource="contract" />

			<status-badge
				v-if="contract?.status && contract.status.length > 0"
				:status="contract.status[0]"
				status-key="stock_check_status" />
		</div>
	</app-title-bar>

	<div class="flex flex-col-reverse 2xl:flex-row gap-4">
		<app-panel class="grow" title="Item Details">
			<template #header>
				<div class="flex flex-col-reverse sm:flex-row items-center gap-4">
					<contract-notes v-if="contract?.status[0].notes">
						{{ contract?.status[0].notes }}
					</contract-notes>

					<contract-notes
						v-if="contract?.status[0].item_notes"
						title="Ancillary Items"
						button="Ancillaries">
						{{ contract?.status[0].item_notes }}
					</contract-notes>

					<app-button size="small" :disabled="loading" @click="confirmQuantities"
						>Confirm Quantities</app-button
					>
					<operators-dropdown v-model="assignOperator" />
					<app-button size="small" :disabled="loading" @click="assignAll()"
						>Assign All</app-button
					>
				</div>
			</template>

			<AssignStockCheckTable
				ref="stockTable"
				:loading="loading"
				:items="items"
				@select="(value: ContractItem[]) => (selections = value)"
				@update="(value: ContractItem) => updateItem(value)"
				@clicked-item="(value: ContractItem) => (currentItem = value)" />

			<div class="mt-5 mb-10 flex justify-end gap-4">
				<app-button icon="fa-plus" @click="adding = true">Add Item Ancillaries</app-button>

				<app-button :disabled="selections.length === 0" @click="save('hire-desk')">
					Send To Hire Desk
				</app-button>
				<app-button
					:disabled="selections.length === 0 || !canSendToOperator"
					@click="save('operator')">
					Send To Operator
				</app-button>
			</div>

			<item-availability-table :contract-item="currentItem" show-title />
		</app-panel>
	</div>

	<slideover
		title="Ancillary Items"
		:open="adding"
		expandable
		:actions="[{ text: 'Done', method: close }]"
		@close="adding = false">
		<app-input
			v-model="additionalItems"
			label="Additional Items"
			name="additional_items"
			type="textarea" />
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'
	import AssignStockCheckTable from '@/components/datatables/yard/stock/AssignStockCheckTable.vue'
	import ResourceDetails from '@/components/partials/panels/ResourceDetails.vue'
	import OperatorsDropdown from '@/components/partials/dropdowns/OperatorsDropdown.vue'
	import useContracts from '@/composables/contracts'
	import StatusBadge from '@/components/partials/cat2/StatusBadge.vue'
	import { useRoute } from 'vue-router'
	import useOperatorStore from '@/stores/operators'
	import useToasterStore from '@/stores/toaster'
	import type { Row } from '@/data/types/datatable'
	import type { Contract } from '@/data/schema/contract'
	import type { ContractItem } from '@/data/schema/contract-item'
	import ContractService from '@/services/contract'
	import { formatItemsDates } from '@/utils/date'
	import type { StatusUpdate } from '@/data/interfaces/status-update'
	import useAuthStore from '@/stores/auth'
	import { updateStatusParamOnItems } from '@/utils/contract-items'
	import ItemAvailabilityTable from '@/components/datatables/all/ItemAvailabilityTable.vue'

	// Resources
	const api = new ContractService()
	const route = useRoute()
	const toaster = useToasterStore()
	const operatorStore = useOperatorStore()
	const authStore = useAuthStore()

	// Data
	const loading = ref(true)
	const contractNumber = ref()
	const contract = ref<Contract>()
	const items = ref<ContractItem[]>([])
	const currentItem = ref<ContractItem>()
	const assignOperator = ref('')
	const stockTable = ref()
	const selections = ref<Row[]>([])
	const adding = ref(false)
	const additionalItems = ref('')

	const canSendToOperator = computed(() => {
		return selections.value.every(
			(obj) =>
				obj.stock_check_status[0].assigned_to !== null &&
				obj.stock_check_status[0].assigned_to !== undefined
		)
	})

	/**
	 * Get contract and contract item data
	 */
	async function getData() {
		loading.value = true
		selections.value = []

		const { fetchData } = useContracts()

		try {
			const data = await fetchData(contractNumber.value, {
				withItems: false,
				withStockCheckItems: true,
				withContractStatus: true,
				withStockCheckStatus: true,
				depot: authStore.user?.current_depot
			})
			contract.value = data.contract
			items.value = formatItemsDates(data.items)

			if (
				contract.value.status &&
				contract.value.status[0] &&
				contract.value.status[0].item_notes
			) {
				additionalItems.value = contract.value.status[0].item_notes
			}

			items.value = items.value.map((item) => ({
				...item,
				stock_status: item.stock_check_status?.[0].status
			}))

			loading.value = false
		} catch (error) {
			console.error(error)
			loading.value = false
		}
	}

	/**
	 * Set all contract items to assignOperator value
	 */
	function assignAll() {
		updateStatusParamOnItems(items.value, 'stock-check', 'assigned_to', assignOperator.value)
	}

	/**
	 * Set all confirmed quantities to
	 * required quantity values
	 */
	function confirmQuantities() {
		updateStatusParamOnItems(
			items.value,
			'stock-check',
			'quantity_confirmed',
			'quantity_required',
			true
		)
	}

	// Filter items by ID
	function getItemById(id: string) {
		return items.value?.filter((obj) => {
			return obj.id === id
		})[0]
	}

	function updateItem(value: Row) {
		let item = getItemById(value.id)

		if (item && item.stock_check_status) {
			item.stock_check_status[0].assigned_to = value.assigned_to
		}
	}

	function close() {
		adding.value = false
	}

	function save(type: 'hire-desk' | 'operator') {
		let saveItems = []

		if (type == 'hire-desk') {
			saveItems = selections.value.map((item) => ({
				id: item.id,
				status_id: item.stock_check_status?.[0].id,
				quantity_confirmed: item.stock_check_status?.[0].quantity_confirmed,
				item_notes: additionalItems.value
			}))
		} else {
			saveItems = selections.value.map((item) => ({
				id: item.id,
				status_id: item.stock_check_status?.[0].id,
				assigned_to: item.stock_check_status?.[0].assigned_to,
				status: 1
			}))
		}

		toaster.working({
			text: 'Saving contract item changes...',
			dismissable: false,
			timeout: 0
		})

		api.bulkUpdateStatus(saveItems as StatusUpdate[], 'stock-status')
			.then(() => {
				toaster.clear()
				toaster.success({ text: 'Contract saved successfully!' })
				getData()
			})
			.catch((error: any) => {
				toaster.clear()
				toaster.error({ text: 'Error occurred updating contract' })
				console.error(error)
			})
	}

	onMounted(() => {
		operatorStore.set(authStore.user!.current_depot!)
		contractNumber.value = route.params.id

		if (contractNumber.value) {
			getData()
		}
	})
</script>
